import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { clearOnLogout, getCurrentUser, getToken } from "@/lib/utils";
import { AUTH_ROUTE_PATHS, JOURNEY_STEP_KEYS, getJourneyPath, resolveAuthRole } from "@/pages/Auth/authConfig";
import { getSignUpJourneyDraft } from "@/pages/Auth/signUpJourney";
import Loader from "@/components/Loader/Loader";
import LogoutIcon from "@/components/SVGComponents/LogoutIcon";
import WaitlistIllustration from "@/components/SVGComponents/WaitlistIllustration";
import LogoutConfirmModal from "@/components/LogoutConfirmModal/LogoutConfirmModal";
import styles from "./Waitlist.module.scss";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getOnboardingQuestions } from "@/store/slices/QuestionSlice";
import { fetchUserProfile } from "@/store/slices/profileSlice";
import { isApproved, isBlockedStatus } from "@/types/userStatus";

const Waitlist = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isCheckingJourney, setIsCheckingJourney] = useState(true);
  const { profile } = useAppSelector((state) => state.profile);

  useEffect(() => {
    const draft = getSignUpJourneyDraft();
    if (!draft?.accountCreated || draft.questionsCompleted) {
      setIsCheckingJourney(false);
      return;
    }

    const roleFromDraft = draft.role;
    const roleFromUser = resolveAuthRole(getCurrentUser("role"));
    const role = roleFromDraft ?? roleFromUser;
    if (!role) {
      setIsCheckingJourney(false);
      return;
    }

    const run = async () => {
      try {
        const onboarding = await dispatch(getOnboardingQuestions()).unwrap();

        // If onboarding is already completed, no need to stay here.
        if (onboarding.status === "COMPLETED") {
          navigate("/home", { replace: true });
          return;
        }

        const hasPendingQuestions =
          (onboarding.remainingCount ?? 0) > 0 || onboarding.question !== null;

        if (hasPendingQuestions) {
          navigate(getJourneyPath(JOURNEY_STEP_KEYS.questions, role), { replace: true });
          return;
        }

        navigate(getJourneyPath(JOURNEY_STEP_KEYS.welcome, role), { replace: true });
      } catch {
        // If the API fails, keep the user on the waitlist page.
      } finally {
        setIsCheckingJourney(false);
      }
    };

    void run();
  }, [dispatch, navigate]);

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, []);

  useEffect(() => {
    if (!profile?.status) return;

    if (isApproved(profile.status)) {
      navigate("/home", { replace: true });
      return;
    }

    if (isBlockedStatus(profile.status)) {
      navigate(AUTH_ROUTE_PATHS.rejected, { replace: true });
    }
  }, [profile, navigate]);

  if (!getToken()) {
    return <Navigate replace to={AUTH_ROUTE_PATHS.getStarted} />;
  }

  if (isCheckingJourney) {
    return <Loader />;
  }

  const handleLogout = () => {
    clearOnLogout();
    navigate(AUTH_ROUTE_PATHS.getStarted);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          className={styles.logoutButton}
          onClick={() => setLogoutModalOpen(true)}
          type="button"
        >
          <LogoutIcon className={styles.logoutIcon} />
        </button>
      </header>

      <main className={styles.main}>
        <div className={styles.card}>
          <WaitlistIllustration />

          <h1 className={styles.title}>You're on the Waitlist</h1>
          <p className={styles.description}>
            We review every member to ensure meaningful,
            high-quality dinner experiences with fellow Investor.
          </p>
        </div>

        <div className={styles.bottomSection}>
          <div className={styles.noticeRow}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path
                d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9.75 13.5H8.25V8.25H9.75V13.5ZM9.75 6.75H8.25V5.25H9.75V6.75Z"
                fill="#F76E11"
              />
            </svg>
            <span className={styles.noticeText}>
              You'll be notified once your profile is approved.
            </span>
          </div>

          <button
            className={styles.ctaButton}
            onClick={() =>
              window.open(
                "https://mail.google.com/mail/?view=cm&fs=1&to=info@bombayfoundersclub.com",
                "_blank"
              )
            }
          >
            Contact Us
          </button>
        </div>
      </main>

      <LogoutConfirmModal
        closeModal={setLogoutModalOpen}
        modalOpen={logoutModalOpen}
        onConfirm={handleLogout}
      />
    </div>
  );
};

export default Waitlist;
